import { z } from 'zod';

export const invoicingMonthSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month must be YYYY-MM'),
});
export type InvoicingMonthInput = z.infer<typeof invoicingMonthSchema>;

/**
 * The run accepts an optional payer filter. **Absent means every payer** — that
 * is what keeps old clients and the existing suite working untouched. An empty
 * array is rejected rather than treated as absent: the two readings are
 * opposites, and the wrong one bills everyone the teacher just unticked.
 */
/**
 * Optional per-run due-date override (THI-100): a plain `YYYY-MM-DD` date —
 * the "today-or-future" check needs `nowWallClock()` (the VN wall-clock
 * convention `dueDate`/`startAt` already use), which lives in
 * `@repo/shared-types`, not here — this package stays clock-free, so that
 * check runs in the route handler, not this schema. Absent means the +7-days
 * rule (`DEFAULT_PAYMENT_TERM_DAYS`) stands, unchanged.
 */
export const invoicingRunSchema = invoicingMonthSchema.extend({
  clientIds: z.array(z.string().min(1)).min(1).max(500).optional(),
  dueDate: z.string().date('dueDate must be YYYY-MM-DD').optional(),
});
export type InvoicingRunInput = z.infer<typeof invoicingRunSchema>;

/**
 * Partial reopen (THI-170). Two selection wires, mutually exclusive:
 *
 * - `clientIds` (legacy, payer-grained): same reading as the run above —
 *   **absent means every payer** (today's full reopen, every existing caller
 *   unchanged) — but here an empty array is accepted rather than rejected
 *   (adjudicated, fix-round-1 LOW-4: deliberate, not an oversight). The
 *   picker's confirm button disables at zero selected, so the FE never sends
 *   `[]`; a direct API call that does gets the safe reading (touches nobody)
 *   rather than a 400, matching `reopenMonth`'s own empty-means-nobody
 *   contract. `.max(500)` and the per-element `.min(1)` otherwise mirror
 *   `invoicingRunSchema` above.
 * - `invoiceIds`/`creditClientIds` (fix-round-2, row-grained — what the FE
 *   actually sends now): the wire selection unit must match the PICKER ROW,
 *   not the payer, because one payer can hold more than one invoice this
 *   month (the second-close scenario) — a payer-grained selection could not
 *   tick one of that payer's invoices without also sweeping in the other.
 *   `invoiceIds` names invoices directly; `creditClientIds` names payers by
 *   their invoice-less (pure-credit) periods only, which can never pull in
 *   that same payer's separate invoice. Either or both may be sent (and
 *   either may be `[]`, same empty-means-nobody-on-that-wire reading); at
 *   least one of the two being present is what selects "row-grained mode".
 *
 * Sending `clientIds` together with `invoiceIds` and/or `creditClientIds` is
 * rejected with a 400 (`.refine` below) — the two wires are for different
 * callers (the legacy path and the current picker), not a combinable pair,
 * and silently picking one would be exactly the kind of surprise this
 * endpoint cannot afford.
 */
export const invoicingReopenSchema = invoicingMonthSchema
  .extend({
    clientIds: z.array(z.string().min(1)).max(500).optional(),
    invoiceIds: z.array(z.string().min(1)).max(500).optional(),
    creditClientIds: z.array(z.string().min(1)).max(500).optional(),
  })
  .refine(
    (data) => !(data.clientIds !== undefined && (data.invoiceIds !== undefined || data.creditClientIds !== undefined)),
    { message: 'Gửi clientIds hoặc invoiceIds/creditClientIds, không gửi cả hai', path: ['clientIds'] },
  );
export type InvoicingReopenInput = z.infer<typeof invoicingReopenSchema>;
